/**
 * VendorHome.jsx — Iter 50. Mirrors AgencyHome.jsx.
 * Lists every engagement the vendor has + the founder docs they requested.
 * Shows status banner if vendor is not yet approved.
 */
import React, { useEffect, useState } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { Briefcase, FileText, DownloadSimple, Clock, Check, CaretDown, Lock } from '@phosphor-icons/react';

const bandColors = { pending_review: '#f59e0b', approved: '#10b981', rejected: '#ef4444', paused: '#64748b' };

// (Iter 65) Guided service requests move through more statuses than the legacy
// intro flow, and several of them need the vendor to act — so the chip can't just
// be "green unless pending_payment". The actionable ones live on the Requests tab.
const STATUS_LABEL = {
  agreement_signed: 'founder preparing docs',
  awaiting_quote:   'awaiting your quote',
  quote_ready:      'quote sent',
  pending_payment:  'awaiting payment',
  // (Iter 68) Delivery / review loop.
  delivered:        'delivered — with founder',
  revisions:        'changes requested',
  awaiting_balance: 'approved — balance due',
};
const STATUS_CHIP = {
  agreement_signed: 'bg-text-muted/15 text-text-muted',
  awaiting_quote:   'bg-warning/15 text-warning',
  quote_ready:      'bg-info/15 text-info',
  pending_payment:  'bg-warning/15 text-warning',
  delivered:        'bg-info/15 text-info',
  revisions:        'bg-warning/15 text-warning',
  awaiting_balance: 'bg-info/15 text-info',
  closed:           'bg-success/15 text-success',
};

const VendorHome = ({ vendor, accent }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [docs, setDocs] = useState({});
  const [docsLoading, setDocsLoading] = useState('');
  const [downloading, setDownloading] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/marketplace-engagements/vendor-side');
        if (!cancelled) setRows(r.data.engagements || []);
      } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load engagements'); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  const toggleExpand = async (eng) => {
    if (expanded === eng.id) { setExpanded(null); return; }
    setExpanded(eng.id);
    if (docs[eng.id]) return;
    setDocsLoading(eng.id);
    try {
      const r = await api.get(`/marketplace-engagements/${eng.id}/required-docs`);
      setDocs((prev) => ({ ...prev, [eng.id]: r.data.required_documents || [] }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load docs'); }
    finally { setDocsLoading(''); }
  };

  const download = async (eng, file) => {
    setDownloading(file.id);
    try {
      const r = await api.get(`/marketplace-engagements/${eng.id}/docs/${file.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = file.filename || 'file';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
    finally { setDownloading(''); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1280px] mx-auto" data-testid="vendor-home">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2" style={{ color: accent }}>{vendor.vendor_type === 'service' ? 'Founderboard Services — Vendor' : 'Founderboard Credit — Lender'}</div>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-text-primary leading-[1.05] mb-2" style={{ letterSpacing: '-0.02em' }}>Your engagements</h1>
        <p className="text-sm text-text-secondary">Founders who&rsquo;ve requested an intro through your catalog. Click any card to expand and download founder-uploaded docs.</p>
      </div>

      {/* Status banner — only when not approved (approved is the happy path, no banner needed). */}
      {vendor.status !== 'approved' && (
        <div className="rounded-2xl p-4 mb-6 flex items-center justify-between" style={{ backgroundColor: `${bandColors[vendor.status] || '#64748b'}1a`, borderLeft: `4px solid ${bandColors[vendor.status] || '#64748b'}` }} data-testid="vendor-status-banner">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: bandColors[vendor.status] || '#64748b' }}>Status</div>
            <div className="text-sm font-semibold text-text-primary capitalize">{(vendor.status || '').replace(/_/g, ' ')}</div>
            {vendor.status === 'pending_review' && <p className="text-xs text-text-secondary mt-1">Sid &amp; Lalith will review your application soon. You&rsquo;ll be notified at {vendor.contact_email}.</p>}
            {vendor.status === 'rejected' && <p className="text-xs text-text-secondary mt-1">{vendor.admin_note || 'Reach out to support@founderboard.io for next steps.'}</p>}
          </div>
        </div>
      )}

      <div>
        <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-3 inline-flex items-center gap-1.5"><Briefcase size={11} weight="bold" /> Engagements ({rows.length})</div>
        {loading ? (
          <div className="text-sm text-text-muted py-4">Loading engagements…</div>
        ) : rows.length === 0 ? (
          <div className="border border-dashed border-border rounded-xl p-10 text-center text-sm text-text-muted">
            No founder engagements yet. They&rsquo;ll show up here the moment a founder hits &ldquo;Request intro&rdquo; on your offering.
          </div>
        ) : (
          <div className="space-y-2" data-testid="vendor-engagements">
            {rows.map((e) => {
              const isOpen = expanded === e.id;
              const myDocs = docs[e.id] || [];
              return (
                <div key={e.id} className="bg-surface border border-border rounded-xl p-4" data-testid={`vendor-engagement-${e.id}`}>
                  <div className="flex items-start gap-3 flex-wrap">
                    <span className="w-9 h-9 rounded-lg inline-flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${accent}1a`, color: accent }}>
                      <Briefcase size={16} weight="fill" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-text-primary truncate">{e.founder_startup_name || e.founder_full_name}</div>
                      {e.offering?.catalog_item_name && <div className="text-[11px] text-text-muted truncate">{e.offering.catalog_item_name}</div>}
                      <div className="flex gap-3 text-[10px] text-text-muted mt-1 flex-wrap">
                        <span className={`font-semibold px-1.5 py-0.5 rounded ${STATUS_CHIP[e.status] || 'bg-success/15 text-success'}`}>{STATUS_LABEL[e.status] || (e.status || '').replace(/_/g, ' ')}</span>
                        {e.founder_email && <a href={`mailto:${e.founder_email}`} className="hover:text-text-primary">{e.founder_email}</a>}
                        {e.offering?.delivery_days && <span className="inline-flex items-center gap-0.5"><Clock size={9} weight="bold" /> {e.offering.delivery_days}d</span>}
                      </div>
                      {e.note && <p className="text-[11px] text-text-secondary mt-1 italic">&ldquo;{e.note}&rdquo;</p>}
                    </div>
                  </div>
                  {(e.offering?.required_documents?.length > 0) && (
                    <button onClick={() => toggleExpand(e)} data-testid={`vendor-engagement-docs-${e.id}`}
                      className="mt-2 w-full text-[11px] inline-flex items-center justify-between px-2.5 py-1.5 rounded border border-border bg-background hover:border-text-primary">
                      <span className="inline-flex items-center gap-1.5 text-text-secondary">
                        {e.is_locked ? <Lock size={11} weight="fill" className="text-warning" /> : <FileText size={11} weight="bold" style={{ color: accent }} />}
                        {e.offering.required_documents.length} doc{e.offering.required_documents.length === 1 ? '' : 's'} you requested
                        {e.is_locked && <span className="ml-1 text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 rounded bg-warning/15 text-warning">Locked</span>}
                      </span>
                      <CaretDown size={10} weight="bold" className="text-text-muted" style={{ transform: isOpen ? 'rotate(180deg)' : 'none' }} />
                    </button>
                  )}
                  {isOpen && (
                    <div className="mt-2 p-3 rounded border border-border bg-background" data-testid={`vendor-engagement-docs-panel-${e.id}`}>
                      {e.is_locked ? (
                        <div className="flex items-start gap-2 text-xs text-text-secondary" data-testid={`vendor-engagement-docs-locked-${e.id}`}>
                          <Lock size={13} weight="fill" className="text-warning mt-0.5 flex-shrink-0" />
                          <div>
                            <div className="font-semibold text-text-primary">Founder documents are locked</div>
                            <div className="text-[11px] text-text-muted mt-0.5">
                              {e.flow === 'service_v2'
                                ? 'Files are released as soon as the founder submits this request — you’ll need them to quote, so no payment is required first.'
                                : 'Files will be released as soon as the founder completes the upfront payment for this engagement.'}
                            </div>
                          </div>
                        </div>
                      ) : docsLoading === e.id ? <p className="text-xs text-text-muted">Loading docs…</p> : myDocs.length === 0 ? (
                        <p className="text-xs text-text-muted">No required documents on this engagement.</p>
                      ) : (
                        <div className="space-y-2">
                          {myDocs.map((d) => (
                            <div key={`${d.dataroom_id}::${d.slot_id}`} className="text-xs">
                              <div className="font-semibold text-text-primary">{d.slot_name} <span className="text-text-muted font-normal">&middot; {d.dataroom_name}</span></div>
                              {d.files.length === 0 ? (
                                <div className="text-[11px] text-text-muted italic">Not yet uploaded by founder</div>
                              ) : (
                                <div className="space-y-1 mt-1">
                                  {d.files.map((f) => (
                                    <div key={f.id} className="flex items-center gap-2 bg-surface border border-border rounded px-2 py-1.5">
                                      <Check size={10} weight="bold" className="text-success flex-shrink-0" />
                                      <span className="flex-1 truncate text-[11px]">{f.filename}</span>
                                      <button onClick={() => download(e, f)} disabled={downloading === f.id}
                                        data-testid={`vendor-engagement-download-${f.id}`}
                                        className="text-[10px] font-semibold px-2 py-0.5 rounded inline-flex items-center gap-1 text-white" style={{ backgroundColor: accent }}>
                                        <DownloadSimple size={9} weight="bold" /> {downloading === f.id ? '…' : 'Download'}
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorHome;
